// src/js/components/Shared/ImportWizard/components/RulesDialog.jsx
import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef
} from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  List,
  Typography
} from "@material-ui/core";
import { useGetDetails, useGetRule } from "../hooks/useGetDetails";
import { RulesFactory } from "../helpers/RulesFactory";
import { SuggestionsFactoryMethod } from "../helpers";
import { TYPES_STATE } from "../types/types";

export const RulesDialog = React.memo(
  ({
    client,
    idPrograma,
    open,
    onClose,
    correlation,
    rulesOps,
    onSetCorrelation
  }) => {
    /* 1️⃣  Early exit – no render if dialog is closed or correlation missing */
    if (!open || !correlation) {
      return null;
    }

    /* 2️⃣  Memoized derived values – avoid new references on each render */
    const correlationMemo = useMemo(() => correlation, [correlation]);

    const { index, selected } = correlationMemo;
    const header = useMemo(
      () =>
        selected && selected.header
          ? selected.header
          : { label: "", value: "" },
      [selected]
    );
    const field = useMemo(
      () =>
        selected && selected.field ? selected.field : { label: "", value: "" },
      [selected]
    );
    const rulesSelected = useMemo(
      () => (selected && selected.rules ? selected.rules : []),
      [selected]
    );

    const ruleOp = useMemo(
      () => {
        const found = rulesOps.find(op => op.field === field.value);
        return found || null;
      },
      [rulesOps, field.value]
    );

    const details = useGetDetails({
      op: ruleOp ? ruleOp.query : null,
      client,
      variables: { idPrograma }
    });

    const suggestions = useMemo(
      () => {
        if (!details || !ruleOp) return [];
        const suggestionsRule = SuggestionsFactoryMethod.createSuggestions({
          results: details,
          rule: ruleOp
        });
        return suggestionsRule.getSuggestions();
      },
      [details, ruleOp]
    );

    /* 3️⃣  State + Refs */
    const [rulesItems, setRulesItems] = useState([]);
    const rulesItemsRef = useRef([]);
    const suggestionsRef = useRef([]);
    const hasInitializedRef = useRef(false);

    useEffect(
      () => {
        suggestionsRef.current = suggestions;
      },
      [suggestions]
    );

    /* 4️⃣  Create callbacks that stay stable across renders */
    const createRuleCallbacks = useCallback(
      id => ({
        onRemove: () => {
          setRulesItems(prev => {
            const newItems = prev.filter(r => r.id !== id);
            rulesItemsRef.current = newItems;
            return newItems;
          });
        },
        onUpdate: estado => {
          setRulesItems(prev => {
            const newItems = prev.map(
              r => (r.id === id ? { ...r, estado } : r)
            );
            rulesItemsRef.current = newItems;
            return newItems;
          });
        }
      }),
      []
    );

    /* 5️⃣  Crear reglas al principio cuando `rulesSelected` cambie */
    useEffect(
      () => {
        if (!Array.isArray(rulesSelected)) return;

        const newRules = rulesSelected.map(rule => {
          const id = Date.now() + Math.random();
          const callbacks = createRuleCallbacks(id);
          const ruleItem = RulesFactory.createRule({
            idRule: rule.type,
            suggestions: suggestionsRef.current
          });

          return {
            id,
            ruleItem,
            estado: rule,
            ...callbacks
          };
        });

        setRulesItems(newRules);
        rulesItemsRef.current = newRules;
        hasInitializedRef.current = true;
      },
      [rulesSelected, createRuleCallbacks]
    );

    /* 6️⃣  Actualizar reglas cuando las `suggestions` cambien */
    useEffect(
      () => {
        if (!hasInitializedRef.current || suggestions.length === 0) return;

        const updated = rulesItemsRef.current.map(item => ({
          ...item,
          ruleItem: RulesFactory.createRule({
            idRule: item.estado.type,
            suggestions
          })
        }));

        setRulesItems(updated);
        rulesItemsRef.current = updated;
      },
      [suggestions]
    );

    /* 7️⃣  Handlers */
    const handleClose = useCallback(() => onClose(), [onClose]);

    const handleSubmit = useCallback(
      () => {
        const newRules = rulesItems.map(r => r.estado);
        const correlationActualizado = {
          ...selected,
          rules: newRules
        };
        onSetCorrelation(correlationActualizado, index);
        onClose();
      },
      [rulesItems, selected, index, onSetCorrelation, onClose]
    );

    const handleAddRule = useCallback(
      e => {
        const id = Date.now() + Math.random();
        const callbacks = createRuleCallbacks(id);
        const ruleItem = RulesFactory.createRule({
          idRule: e.currentTarget.id,
          suggestions: suggestionsRef.current
        });

        setRulesItems(prev => {
          const newItems = [
            ...prev,
            {
              id,
              ruleItem,
              estado: TYPES_STATE[e.currentTarget.id],
              ...callbacks
            }
          ];
          rulesItemsRef.current = newItems;
          return newItems;
        });
      },
      [createRuleCallbacks]
    );

    /* 8️⃣  JSX */
    return (
      <Dialog open={open} maxWidth="md" fullWidth scroll="body">
        <DialogTitle>{`Reglas para ${header.label} → ${
          field.label
        }`}</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>Agregar regla:</Typography>
          <Grid container spacing={8}>
            <Grid item xs={6} md={3}>
              <Button
                id="listaIn"
                onClick={handleAddRule}
                variant="outlined"
                fullWidth
              >
                Lista (IN)
              </Button>
            </Grid>
            <Grid item xs={6} md={3}>
              <Button
                id="listaNotIn"
                onClick={handleAddRule}
                variant="outlined"
                fullWidth
              >
                Lista (NOT IN)
              </Button>
            </Grid>
            <Grid item xs={6} md={3}>
              <Button
                id="IgualA"
                onClick={handleAddRule}
                variant="outlined"
                fullWidth
              >
                Igual a
              </Button>
            </Grid>
            <Grid item xs={6} md={3}>
              <Button
                id="RegExp"
                onClick={handleAddRule}
                variant="outlined"
                fullWidth
              >
                RegExp
              </Button>
            </Grid>
            <Grid item xs={6} md={3}>
              <Button
                id="Default"
                onClick={handleAddRule}
                variant="outlined"
                fullWidth
              >
                Por defecto
              </Button>
            </Grid>
          </Grid>
          <List dense style={{ width: "100%" }}>
            {rulesItems.map(
              rule =>
                rule.ruleItem
                  ? rule.ruleItem({
                      key: rule.id, // clave para evitar re‑renders
                      id: rule.id,
                      estado: rule.estado,
                      onRemove: rule.onRemove,
                      onUpdate: rule.onUpdate
                    })
                  : null
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button onClick={handleSubmit}>Guardar</Button>
        </DialogActions>
      </Dialog>
    );
  }
);
